import React, { useEffect, useState } from 'react';

import { barberService } from '../../services/barberService';
import { branchService } from '../../services/branchService';
import { serviceTypeService } from '../../services/serviceTypeService';
import { Barber, DayOfWeek, ScheduleRequest, ServiceType, BarberServiceRequest } from '../../types/barber.types';
import { Branch } from '../../types/branch.types';
import Modal from '../../components/common/Modal';
import { useToast } from '../../context/ToastContext';
import AdminHeader from '../../components/admin/AdminHeader';

interface DayScheduleConfig {
    enabled: boolean;
    startTime: string;
    endTime: string;
    branchId: string;
}

const DAYS_OF_WEEK = [
    { key: DayOfWeek.MONDAY, label: 'Lunes' },
    { key: DayOfWeek.TUESDAY, label: 'Martes' },
    { key: DayOfWeek.WEDNESDAY, label: 'Miércoles' },
    { key: DayOfWeek.THURSDAY, label: 'Jueves' },
    { key: DayOfWeek.FRIDAY, label: 'Viernes' },
    { key: DayOfWeek.SATURDAY, label: 'Sábado' },
    { key: DayOfWeek.SUNDAY, label: 'Domingo' },
];

const BarberApprovalList: React.FC = () => {
    const { showToast } = useToast();
    const [barbers, setBarbers] = useState<Barber[]>([]);
    const [branches, setBranches] = useState<Branch[]>([]);
    const [availableServices, setAvailableServices] = useState<ServiceType[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedBarber, setSelectedBarber] = useState<Barber | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Form Data
    const [submitting, setSubmitting] = useState(false);

    // Services selected state
    const [selectedServiceIds, setSelectedServiceIds] = useState<number[]>([]);
    const [customPrices, setCustomPrices] = useState<Record<number, number>>({});

    // Schedule per day
    const [daySchedules, setDaySchedules] = useState<Record<string, DayScheduleConfig>>({});

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [barbersData, branchesData, servicesData] = await Promise.all([
                barberService.getPendingBarbers(),
                branchService.getAllBranches(),
                serviceTypeService.getAllServices()
            ]);
            setBarbers(barbersData);
            setBranches(branchesData);
            setAvailableServices(servicesData);
        } catch (error) {
            console.error('Error loading data:', error);
            showToast('Error al cargar datos pendientes', 'error');
        } finally {
            setLoading(false);
        }
    };

    const initializeDaySchedules = () => {
        const defaultBranchId = branches.length > 0 ? branches[0].id.toString() : '';
        const schedules: Record<string, DayScheduleConfig> = {};

        DAYS_OF_WEEK.forEach(day => {
            schedules[day.key] = {
                enabled: day.key !== DayOfWeek.SUNDAY,
                startTime: '09:00',
                endTime: day.key === DayOfWeek.SATURDAY ? '14:00' : '19:00',
                branchId: defaultBranchId
            };
        });

        setDaySchedules(schedules);
    };

    const handleOpenApproveModal = (barber: Barber) => {
        setSelectedBarber(barber);
        setIsModalOpen(true);
        setSelectedServiceIds([]);
        setCustomPrices({});
        initializeDaySchedules();
    };

    const toggleService = (serviceId: number) => {
        setSelectedServiceIds(prev =>
            prev.includes(serviceId)
                ? prev.filter(id => id !== serviceId)
                : [...prev, serviceId]
        );
    };

    const handleCustomPriceChange = (serviceId: number, price: string) => {
        const numericPrice = parseFloat(price);
        setCustomPrices(prev => ({
            ...prev,
            [serviceId]: isNaN(numericPrice) ? 0 : numericPrice
        }));
    };

    const updateDaySchedule = (day: string, field: keyof DayScheduleConfig, value: string | boolean) => {
        setDaySchedules(prev => ({
            ...prev,
            [day]: {
                ...prev[day],
                [field]: value
            }
        }));
    };

    const handleApprove = async () => {
        if (!selectedBarber) return;

        if (selectedServiceIds.length === 0) {
            showToast('Debe seleccionar al menos un servicio para el barbero', 'warning');
            return;
        }

        // Build schedules
        const schedules: ScheduleRequest[] = [];
        Object.entries(daySchedules).forEach(([dayOfWeek, config]) => {
            if (config.enabled && config.branchId) {
                schedules.push({
                    dayOfWeek,
                    startTime: config.startTime,
                    endTime: config.endTime,
                    branchId: parseInt(config.branchId)
                });
            }
        });

        if (schedules.length === 0) {
            showToast('Debe habilitar al menos un día de trabajo', 'warning');
            return;
        }

        // Build services
        const services: BarberServiceRequest[] = selectedServiceIds.map(id => ({
            serviceTypeId: id,
            customPrice: customPrices[id] || undefined
        }));

        setSubmitting(true);
        try {
            await barberService.approveBarber({
                barberId: selectedBarber.id,
                pricePerService: 0, // Deprecated
                schedules,
                services
            });

            showToast('Barbero aprobado con éxito', 'success');
            setIsModalOpen(false);
            loadData();
        } catch (error) {
            console.error('Error approving barber:', error);
            showToast('Error al aprobar barbero', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <AdminHeader
                title="✅ Aprobación de Barberos"
                rightContent={
                    <button className="btn btn-outline btn-sm" onClick={loadData}>Actualizar</button>
                }
            />
            <div className="container fade-in">

                {loading ? (
                    <div className="flex justify-center p-xl">
                        <div className="spinner"></div>
                    </div>
                ) : barbers.length === 0 ? (
                    <div className="card text-center p-xl">
                        <p className="text-secondary mb-md">No hay barberos pendientes de aprobación.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-md">
                        {barbers.map(barber => (
                            <div key={barber.id} className="card">
                                <div className="flex items-center gap-md mb-md">
                                    <div className="bg-gray-200 rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl text-gray-600">
                                        {barber.firstName.charAt(0)}{barber.lastName.charAt(0)}
                                    </div>
                                    <div>
                                        <h3 className="m-0 font-bold">{barber.firstName} {barber.lastName}</h3>
                                        <p className="text-sm text-secondary">{barber.email}</p>
                                    </div>
                                </div>

                                <div className="mb-md">
                                    <p className="text-sm mb-xs"><span className="font-semibold">Especialidad:</span> {barber.specialization || 'No especificada'}</p>
                                    <p className="text-sm mb-xs"><span className="font-semibold">Experiencia:</span> {barber.yearsOfExperience} años</p>
                                </div>

                                <button className="btn btn-primary w-full" onClick={() => handleOpenApproveModal(barber)}>
                                    Revisar y Aprobar
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <Modal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    title={`Aprobar a ${selectedBarber?.firstName} ${selectedBarber?.lastName}`}
                >
                    <div style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: '10px' }}>

                        <h3 className="mb-sm">Servicios que brindará</h3>
                        <p className="text-sm text-secondary mb-md">
                            Selecciona los servicios del catálogo. Si dejas el precio vacío, se usará el sugerido.
                        </p>

                        <div className="flex flex-column gap-sm mb-lg">
                            {availableServices.map(service => {
                                const isSelected = selectedServiceIds.includes(service.id);
                                return (
                                    <div
                                        key={service.id}
                                        className="p-sm rounded border flex items-center gap-md"
                                        style={{
                                            borderColor: isSelected ? 'var(--accent)' : 'rgba(0,0,0,0.1)',
                                            background: isSelected ? 'rgba(0,0,0,0.02)' : 'transparent'
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => toggleService(service.id)}
                                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                        />
                                        <div className="flex-1">
                                            <div className="font-semibold">{service.name}</div>
                                            <div className="text-xs text-secondary">Sugerido: ${service.suggestedPrice}</div>
                                        </div>
                                        {isSelected && (
                                            <div className="flex flex-column gap-xs" style={{ width: '120px' }}>
                                                <label className="text-xs text-secondary">Precio Custom</label>
                                                <input
                                                    type="number"
                                                    className="form-input p-xs text-sm"
                                                    placeholder={service.suggestedPrice.toString()}
                                                    value={customPrices[service.id] || ''}
                                                    onChange={(e) => handleCustomPriceChange(service.id, e.target.value)}
                                                />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <h3 className="mb-sm border-t pt-md">Horario y Sucursal por Día</h3>
                        <p className="text-sm text-secondary mb-sm">
                            Configura el horario y sucursal para cada día de la semana.
                        </p>

                        <div className="mb-md">
                            {DAYS_OF_WEEK.map(day => (
                                <div
                                    key={day.key}
                                    className="p-sm mb-sm rounded"
                                    style={{
                                        background: daySchedules[day.key]?.enabled
                                            ? 'rgba(34, 197, 94, 0.1)'
                                            : 'rgba(156, 163, 175, 0.1)',
                                        border: '1px solid',
                                        borderColor: daySchedules[day.key]?.enabled
                                            ? 'rgba(34, 197, 94, 0.3)'
                                            : 'rgba(156, 163, 175, 0.2)'
                                    }}
                                >
                                    <div className="flex items-center gap-sm mb-sm">
                                        <input
                                            type="checkbox"
                                            checked={daySchedules[day.key]?.enabled || false}
                                            onChange={(e) => updateDaySchedule(day.key, 'enabled', e.target.checked)}
                                            style={{ width: '18px', height: '18px' }}
                                        />
                                        <span className="font-semibold" style={{ minWidth: '80px' }}>{day.label}</span>

                                        {daySchedules[day.key]?.enabled && (
                                            <>
                                                <input
                                                    type="time"
                                                    value={daySchedules[day.key]?.startTime || '09:00'}
                                                    onChange={(e) => updateDaySchedule(day.key, 'startTime', e.target.value)}
                                                    className="form-input"
                                                    style={{ width: '110px', padding: '4px 8px' }}
                                                />
                                                <span>-</span>
                                                <input
                                                    type="time"
                                                    value={daySchedules[day.key]?.endTime || '19:00'}
                                                    onChange={(e) => updateDaySchedule(day.key, 'endTime', e.target.value)}
                                                    className="form-input"
                                                    style={{ width: '110px', padding: '4px 8px' }}
                                                />
                                            </>
                                        )}
                                    </div>

                                    {daySchedules[day.key]?.enabled && (
                                        <div className="flex items-center gap-sm">
                                            <span className="text-sm" style={{ minWidth: '80px', marginLeft: '26px' }}>Sucursal:</span>
                                            <select
                                                className="form-select flex-1"
                                                value={daySchedules[day.key]?.branchId || ''}
                                                onChange={(e) => updateDaySchedule(day.key, 'branchId', e.target.value)}
                                                style={{ padding: '4px 8px' }}
                                            >
                                                {branches.map(branch => (
                                                    <option key={branch.id} value={branch.id}>
                                                        {branch.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex justify-end gap-sm mt-lg">
                        <button className="btn btn-outline" onClick={() => setIsModalOpen(false)} disabled={submitting}>
                            Cancelar
                        </button>
                        <button className="btn btn-primary" onClick={handleApprove} disabled={submitting}>
                            {submitting ? 'Aprobando...' : 'Confirmar Aprobación'}
                        </button>
                    </div>
                </Modal>
            </div>
        </>
    );
};

export default BarberApprovalList;
